// React Imports
import { useState, useEffect } from "react";

// External Libraries
import { Divider } from "primereact/divider";
import { Button } from "primereact/button";

// Local Components
import { DotProgress } from "sj-ui-toolkit/src/components/DotProgress/DotProgress";
import { MiniBarChart } from "sj-ui-toolkit/src/components/MiniBarChart/MiniBarChart";
import { AchievementsSkeleton } from "./AchievementsSkeleton";

// Hooks & Helpers
import { fetchProfileAchievements } from "sj-ui-toolkit/src/api";
import { useSmartNavigation } from "sj-ui-toolkit/src/helpers/navigation";
import { getDeltaComparisonText } from "sj-ui-toolkit/src/utils/general";

// Constants
import { INTERNAL_ROUTES } from "sj-ui-toolkit/src/constants/routes";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";

export function AchievementsTab() {
  useBreadcrumb({ label: "Achievements", route: "/profile/achievements", display: "page" });
  const [isLoading, setIsLoading] = useState(true);
  const [userAchievementData, setUserAchievementData] = useState(null);
  const { paths = {}, lessons = {}, tournaments = {} } = userAchievementData || {};
  const { navigateTo } = useSmartNavigation();

  // Paths Data
  const completedPathsCount = paths.pathsCompleted;
  const pathsEnrolledCount = paths.pathsEnrolled;
  const recentlyCompletedPaths = paths.recentlyCompleted;

  // Lessons Data
  const lessonsCompleted = lessons.completed;
  const lessonsCompletedLast30Days = lessons.recentlyCompleted;
  const lessonsCompletedDifference = lessons.changeOverPrevious;
  const monthlyLessonCompletions = lessons.monthlyLessonCompletions || {};
  const monthlyCompletionCounts = Object.values(monthlyLessonCompletions);
  const monthlyCompletionLabels = Object.keys(monthlyLessonCompletions);
  const lessonsDelta = lessonsCompletedDifference ?? 0;
  const lessonsComparisonText = getDeltaComparisonText(lessonsDelta);

  // Tournaments Data
  const tournamentsCompleted = tournaments.completed;
  const tournamentsRecentlyCompleted = tournaments.recentlyCompleted;

  useEffect(() => {
    const fetchUserAchievements = async () => {
      try {
        const data = await fetchProfileAchievements();
        setUserAchievementData(data);
      } catch (error) {
        console.error("Error fetching user achievement data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserAchievements();
  }, []);

  if (isLoading) {
    return <AchievementsSkeleton />;
  }

  return (
    <div className="flex flex-col gap-[45px] pb-10 px-[40px]">
      <div className="flex gap-[250px]">
        <section
          aria-labelledby="paths-heading"
          className="flex flex-col pr-2 gap-3 min-w-[250px] w-[35%]"
        >
          <h2 id="paths-heading" className="text-muted-aaa body-sm">
            Total paths completed
          </h2>
          <p className="atlas-h2">{completedPathsCount} paths</p>
          <DotProgress total={pathsEnrolledCount} completed={completedPathsCount} />
          <div>
            <p className="body-sm">{recentlyCompletedPaths} completed in the last 30 days</p>
            <p className="body-sm text-muted-aaa">{pathsEnrolledCount} started since joining</p>
          </div>
          <Button
            label="View Certificates"
            severity="secondary"
            className="w-fit"
            onClick={() => navigateTo(INTERNAL_ROUTES.User.routes.certificates.route)}
          />
        </section>
        <section aria-labelledby="lessons-heading" className="flex flex-col pl-2 gap-3">
          <h2 id="lessons-heading" className="text-muted-aaa body-sm">
            Total lessons completed
          </h2>
          <p className="atlas-h2">{lessonsCompleted} lessons</p>
          <MiniBarChart values={monthlyCompletionCounts} labels={monthlyCompletionLabels} />
          <div>
            <p className="body-sm">{lessonsCompletedLast30Days} completed in the last 30 days</p>
            <p className="body-sm text-muted-aaa">{lessonsComparisonText}</p>
          </div>
          <Button
            label="View History"
            severity="secondary"
            className="w-fit"
            onClick={() => navigateTo(INTERNAL_ROUTES.User.routes.history.route)}
          />
        </section>
      </div>
      <Divider className="border-t border-gray-400" />
      <section aria-labelledby="tournaments-heading" className="flex flex-col gap-3">
        <h2 id="tournaments-heading" className="text-muted-aaa body-sm">
          Total tournaments completed
        </h2>
        <p className="atlas-h2">{tournamentsCompleted} tournaments</p>
        <div>
          <p className="body-sm">{tournamentsRecentlyCompleted} completed in the last 30 days</p>
        </div>
      </section>
    </div>
  );
}
